

import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, setDoc, onSnapshot, serverTimestamp, deleteDoc } from "firebase/firestore";
import { storage, db } from "../firebase";

export interface PendingUploadResult {
    raceId?: string;
    categoryId?: string;
    isGroupRide?: boolean;
    message?: string;
}

export const storageService = {
    /**
     * Uploads a race file to Firebase Storage
     * Path: races/{raceId}/files/{userId}/{filename}
     */
    uploadRaceFile: async (raceId: string, userId: string, file: File): Promise<string> => {
        try {
            // Create a unique filename to prevent overwrites if same name uploaded twice
            const timestamp = Date.now();
            const uniqueName = `${timestamp}-${file.name}`;
            const storageRef = ref(storage, `races/${raceId}/files/${userId}/${uniqueName}`);
            
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            
            return downloadURL;
        } catch (error) {
            console.error("Error uploading file to storage:", error);
            throw error;
        }
    },

    /**
     * Uploads a user profile picture to Firebase Storage
     * Path: users/{userId}/profile/{uniqueName}
     */
    uploadProfilePicture: async (userId: string, file: File): Promise<string> => {
        try {
            // Use a timestamped name to avoid caching issues when updating
            const uniqueName = `profile-${Date.now()}-${file.name}`;
            const storageRef = ref(storage, `users/${userId}/profile/${uniqueName}`);
            
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            
            return downloadURL;
        } catch (error) {
            console.error("Error uploading profile picture:", error);
            throw error;
        }
    },

    /**
     * Uploads a file to the pending_uploads folder for auto-matching by the backend.
     * Path: users/{targetUserId}/pending_uploads/{uploadId}_{filename}
     */
    uploadPendingRaceFile: async (
        file: File, 
        targetUserId: string, 
        onStatusUpdate?: (status: { message: string; type: 'success' | 'error' | 'info' }) => void
    ): Promise<PendingUploadResult> => {
        const validExtensions = ['.fit', '.gpx', '.tcx'];
        const fileName = file.name.toLowerCase();
        const isValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

        if (!isValidExtension) {
            throw new Error('Invalid file format. Please upload a .FIT, .GPX, or .TCX file.');
        }

        const MAX_FILE_SIZE_MB = 10;
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            throw new Error(`File is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
        }

        // Magic bytes validation
        const buffer = await file.slice(0, 12).arrayBuffer();
        const uint8Array = new Uint8Array(buffer);
        let isValidSignature = false;
        
        if (fileName.endsWith('.fit')) {
            const decoder = new TextDecoder('utf-8');
            const signature = decoder.decode(uint8Array.slice(8, 12));
            if (signature === '.FIT') {
                isValidSignature = true;
            }
        } else if (fileName.endsWith('.gpx') || fileName.endsWith('.tcx')) {
            const decoder = new TextDecoder('utf-8');
            const snippet = decoder.decode(uint8Array).trim();
            if (snippet.startsWith('<')) {
                isValidSignature = true;
            }
        }

        if (!isValidSignature) {
            throw new Error('Invalid file contents. The file does not appear to be a valid FIT, GPX, or TCX file.');
        }

        const uploadId = Date.now().toString();
        const statusDocRef = doc(db, `users/${targetUserId}/upload_status/${uploadId}`);
        
        try {
            await setDoc(statusDocRef, {
                status: 'processing',
                filename: file.name,
                createdAt: serverTimestamp()
            });

            onStatusUpdate?.({ message: 'Uploading file...', type: 'info' });

            const fileRef = ref(storage, `users/${targetUserId}/pending_uploads/${uploadId}_${file.name}`);
            await uploadBytes(fileRef, file);

            onStatusUpdate?.({ message: "File uploaded! Processing...", type: 'info' });

            return new Promise<PendingUploadResult>((resolve, reject) => {
                const unsubscribe = onSnapshot(statusDocRef, (snapshot) => {
                    if (snapshot.exists()) {
                        const data = snapshot.data();
                        if (data.status === 'success') {
                            onStatusUpdate?.({ message: data.message || "Upload complete! Activity matched.", type: 'success' });
                            unsubscribe();
                            deleteDoc(statusDocRef).catch(console.error);
                            resolve({
                                raceId: data.raceId,
                                categoryId: data.categoryId,
                                isGroupRide: data.isGroupRide,
                                message: data.message,
                            });
                        } else if (data.status === 'error') {
                            onStatusUpdate?.({ message: data.message || "Upload failed during processing.", type: 'error' });
                            unsubscribe();
                            deleteDoc(statusDocRef).catch(console.error);
                            reject(new Error(data.message));
                        }
                    }
                }, (error) => {
                    console.error("Snapshot error:", error);
                    unsubscribe();
                    reject(error);
                });
            });

        } catch (error) {
            console.error('Manual upload failed:', error);
            throw error;
        }
    }
};